import React, { useState } from "react";
import { DiliTextCP } from "../../Utility/DiliTextCP";
import { getShortageReport } from "../../../services/deliveryService";
import handleApiError from "../../../helpers/handleApiError";
import { add as dateAdd, format as dateFormat, parseISO } from "date-fns";
import { NeverScannedResultsViewV2Table } from "./NeverScannedResultsViewV2Table";
import {
  deriveCODTypeFromDeliveryInfo,
  deriveDeliveryTypeFromRefNo,
  exportDataToExcel,
  getDifferenceInDaysForDateInputStrings,
  omitReturnsAndBuyoutsFromNeverScannedReport,
} from "../../../helpers/utilities";
import { viewStates } from "../../../constants";
import { OperationLogsTable } from "./OperationLogs/OperationLogsTable";
import { toast } from "react-toastify";
// import { getClientJWTInfo } from "../../../services/accountService";
import { SearchFormWarning } from "../../Utility/SearchFormWarning";
import { ExportIcon } from "../../Utility/Icons/ExportIcon";

import withDocumentTitle from "../../withDocumentTitle";

const shortageReportTypes = {
  neverScanned: "neverScanned",
  missingAtLatestLocation: "missingAtLatestLocation",
  operationLogs: "operationLogs",
};
const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const dateRangeLimit = 30; //In days

const ShortageReport = ({ setLoading }) => {
  const [deliveryDateSearchFormValues, setDeliveryDateSearchFormValues] =
    useState({
      dateFrom: dateFormat(dateAdd(new Date(), { days: -5 }), "yyyy-MM-dd"),
      dateTo: dateFormat(dateAdd(new Date(), { days: 2 }), "yyyy-MM-dd"),
    });
  const [lastSearchFormValues, setLastSearchFormValues] = useState({
    dateFrom: dateFormat(dateAdd(new Date(), { days: -5 }), "yyyy-MM-dd"),
    dateTo: dateFormat(dateAdd(new Date(), { days: 2 }), "yyyy-MM-dd"),
  });
  const handleChangeDeliveryDate = (e) => {
    setDeliveryDateSearchFormValues({
      ...deliveryDateSearchFormValues,
      [e.target.name]: e.target.value,
    });
  };

  const [currentReportType, setCurrentReportType] = useState(
    shortageReportTypes.neverScanned
  );
  const handleChangeReportType = (e) => {
    // setSearched(false);
    setCurrentReportType(e.target.value);
  };

  // const [shortageReportResults, setShortageReportResults] = useState();
  const [shortBarcodeObjects, setShortBarcodeObjects] = useState([]);
  const [operationLogs, setOperationLogs] = useState([]);
  const [viewState, setViewState] = useState(viewStates.initial);
  const handleClickSearchForShortages = async () => {
    setViewState(viewStates.loading);
    setShortBarcodeObjects([]);
    setOperationLogs([]);
    setLastSearchFormValues(deliveryDateSearchFormValues);
    try {
      const shortageReportResponse = await getShortageReport(
        deliveryDateSearchFormValues.dateFrom,
        deliveryDateSearchFormValues.dateTo
      );
      // console.log(shortageReportResponse);

      const shortageList = shortageReportResponse.data.shortageList;
      const trueShortageBarcodes =
        shortageReportResponse.data.trueShortageBarcodes;
      const deliveryInformationArray =
        shortageReportResponse.data.deliveryInformation;
      const operationLogs = shortageReportResponse.data.opsInformation;

      setOperationLogs(
        operationLogs.filter((log) => {
          return log.type !== "Comment";
        })
      );

      // setShortageReportResults(shortageList);
      const barcodesObject = {};
      //loop through barcodes and assemble an object with its scans (basically grouping)

      shortageList?.map((barcodeStop) => {
        // console.log(barcodeStop.deliveryId);
        // console.log(barcodeStop);
        //if our object does not have this barcode already, initialize it
        if (!barcodesObject[barcodeStop.barcode]) {
          barcodesObject[barcodeStop.barcode] = {
            barcode: barcodeStop.barcode,
            deliveryInfo: deliveryInformationArray.find((deliveryInfo) => {
              return (
                parseInt(deliveryInfo.tranid) ===
                parseInt(barcodeStop.deliveryId)
              );
            }),
            scans:
              barcodeStop.scannedWhen !== null && barcodeStop.scannedWhen !== ""
                ? [
                    {
                      stopType: barcodeStop.stopType,
                      scannedWhen: barcodeStop.scannedWhen,
                      statusCode: barcodeStop.statusCode,
                    },
                  ]
                : [],
          };
        } else if (
          //if it does, and this stop had a scannedWhen, push this stop into our scans array for this barcode
          barcodeStop.scannedWhen !== null &&
          barcodeStop.scannedWhen !== ""
        ) {
          barcodesObject[barcodeStop.barcode].scans.push({
            stopType: barcodeStop.stopType,
            scannedWhen: barcodeStop.scannedWhen,
            statusCode: barcodeStop.statusCode,
          });
        }
      });

      const shortBarcodesArray = [];
      for (const barcode in barcodesObject) {
        if (barcodesObject[barcode].scans.length === 0) {
          shortBarcodesArray.push(barcodesObject[barcode]);
        }
      }
      // console.log(deliveryInformationArray);
      // console.log(shortBarcodesArray);

      //for Clients other than Sam Packs, filter out returns and buyouts from the shortage reports, as currently scanning them is iffy.
      if (omitReturnsAndBuyoutsFromNeverScannedReport()) {
        const barcodesArrayAfterFilteringBuyoutsAndReturnsOut =
          shortBarcodesArray.filter((barcodeObject) => {
            return (
              deriveDeliveryTypeFromRefNo(barcodeObject.deliveryInfo?.refno) ===
              "Regular"
            );
          });
        setShortBarcodeObjects(barcodesArrayAfterFilteringBuyoutsAndReturnsOut);
      } else {
        setShortBarcodeObjects(shortBarcodesArray);
      }
      setViewState(viewStates.success);
    } catch (ex) {
      handleApiError(ex);
      setViewState(viewStates.error);
    }
  };

  const handleClickExportReport = async () => {
    if (currentReportType === shortageReportTypes.neverScanned) {
      if (shortBarcodeObjects.length > 0) {
        //Adding Custom Heading per
        //https://stackoverflow.com/questions/52215676/sheetjs-json-to-sheet-renaming-headers
        const sheetHeaders = [
          "Order Created",
          "Expected Delivery Date",
          "Invoice",
          "Barcode",
          "Customer Name",
          "Customer Address",
          "Customer City",
          "Customer State",
          "Customer Zip",
          // "Customer Phone",
          "Route",
          "COD",
          "Delivery Type",
        ];
        const dataToExport = shortBarcodeObjects.map((barcodeObject) => {
          return {
            orderCreated: dateFormat(
              parseISO(barcodeObject.deliveryInfo?.entered),
              "MM/dd/yyyy"
            ),
            deliveryDate: dateFormat(
              parseISO(barcodeObject.deliveryInfo?.tdate),
              "MM/dd/yyyy"
            ),
            invoice: barcodeObject.deliveryInfo?.invoice,
            barcode: barcodeObject.barcode,
            customerName: barcodeObject.deliveryInfo?.customer,
            customerAddress: barcodeObject.deliveryInfo?.address,
            customerCity: barcodeObject.deliveryInfo?.city,
            customerState: barcodeObject.deliveryInfo?.st,
            customerZip: barcodeObject.deliveryInfo?.zip,
            // customerPhone: barcodeObject.deliveryInfo?.phone,
            route: barcodeObject.deliveryInfo?.route,
            COD: deriveCODTypeFromDeliveryInfo(barcodeObject.deliveryInfo),
            deliveryType: deriveDeliveryTypeFromRefNo(
              barcodeObject.deliveryInfo?.refno
            ),
          };
        });
        exportDataToExcel(
          dataToExport,
          sheetHeaders,
          `DIL-Discrepancy Report (Never Scanned) - ${lastSearchFormValues.dateFrom} to ${lastSearchFormValues.dateTo}`,
          "DIL-Never Scanned"
        );
      } else {
        toast.warn("No results to export.");
      }
    } else if (currentReportType === shortageReportTypes.operationLogs) {
      if (operationLogs.length > 0) {
        const sheetHeaders = [
          "Log Type",
          "Delivery ID",
          "Invoice",
          "Customer",
          "City",
          "Comment",
          "Note",
          "Manifest",
          "Actual",
          "COD",
          "Log Entered",
        ];
        //copy
        let dataToExport = [...operationLogs];
        //sort
        dataToExport.sort((a, b) => {
          return parseISO(b.entered) - parseISO(a.entered);
        });
        //reorder columns
        dataToExport = dataToExport.map((log) => {
          return {
            type: log.type,
            deliveryID: log.dtranid,
            invoice: log.invoice,
            customer: log.customer,
            city: log.city,
            comment: log.comment,
            note: log.note,
            man: log.man.trim(),
            act: log.act.trim(),
            cod: currencyFormatter.format(log.cod),
            entered: dateFormat(parseISO(log.entered), "MM/dd/yyyy hh:mmaa"),
          };
        });
        exportDataToExcel(
          dataToExport,
          sheetHeaders,
          `DIL-Discrepancy Report (Operation Logs) - ${lastSearchFormValues.dateFrom} to ${lastSearchFormValues.dateTo}`,
          "DIL-Operation Logs"
        );
      } else {
        toast.warn("No results to export.");
      }
    }
  };
  const dateRangeOverLimit =
    getDifferenceInDaysForDateInputStrings(
      deliveryDateSearchFormValues.dateTo,
      deliveryDateSearchFormValues.dateFrom
    ) > dateRangeLimit;

  const fromAndToMixed =
    deliveryDateSearchFormValues.dateTo < deliveryDateSearchFormValues.dateFrom;

  const datesInvalid = dateRangeOverLimit || fromAndToMixed;
  //parseISO seems to work better, normal Date.parse was giving me 1 day before than the actual date. Probably due to some time zone thing.
  // console.log(parseISO(deliveryDateSearchFormValues.dateTo));
  return (
    <div>
      <div className="tw-text-center tw-mb-5 lg:tw-absolute lg:tw-top-0 lg:tw-right-0">
        <div className="tw-flex tw-justify-center tw-gap-x-4 lg:tw-items-center">
          <DiliTextCP
            type="select"
            value={currentReportType}
            style={{ maxWidth: "fit-content", textOverflow: "ellipsis" }}
            onChange={handleChangeReportType}
          >
            <option value={shortageReportTypes.neverScanned}>
              Never Scanned
            </option>
            <option value={shortageReportTypes.operationLogs}>
              Operation Logs
            </option>
          </DiliTextCP>

          <ExportIcon
            role="button"
            className="no-copy"
            size={25}
            alt="Export Orders"
            title="Export Report"
            onClick={handleClickExportReport}
          />
        </div>
      </div>

      <div className="tw-mb-4 lg:tw-mb-12 lg:tw-mt-8 container-lg">
        <div className="tw-flex tw-flex-col tw-gap-y-3 lg:tw-flex-row lg:tw-justify-evenly lg:tw-items-end lg:tw-gap-x-12">
          <DiliTextCP
            className="tw-flex-1"
            required
            type="date"
            label="Delivery From Date"
            // max={dateFormat(
            //   dateAdd(parseISO(deliveryDateSearchFormValues.dateTo), {
            //     days: 0,
            //   }),
            //   "yyyy-MM-dd"
            // )}
            name="dateFrom"
            onChange={handleChangeDeliveryDate}
            value={deliveryDateSearchFormValues.dateFrom}
          />
          <DiliTextCP
            className="tw-flex-1"
            required
            type="date"
            label="Delivery To Date"
            // max={dateFormat(dateAdd(new Date(), { days: 5 }), "yyyy-MM-dd")}
            name="dateTo"
            onChange={handleChangeDeliveryDate}
            value={deliveryDateSearchFormValues.dateTo}
          />

          <div className="tw-self-center lg:tw-self-auto">
            <button
              className="btn btn-primary"
              onClick={handleClickSearchForShortages}
              disabled={datesInvalid}
            >
              Search
            </button>
          </div>
        </div>

        <SearchFormWarning
          invalid={datesInvalid}
          message={
            fromAndToMixed
              ? "To date is before from date"
              : dateRangeOverLimit
              ? `Date range cannot be over ${dateRangeLimit} days`
              : ""
          }
        />
      </div>

      {/* {console.log(shortBarcodeObjects.length)} */}
      {currentReportType === shortageReportTypes.neverScanned && (
        <NeverScannedResultsViewV2Table
          neverScannedResults={shortBarcodeObjects}
          viewState={viewState}
        />
      )}
      {currentReportType === shortageReportTypes.operationLogs && (
        <OperationLogsTable
          operationLogs={operationLogs}
          viewState={viewState}
        />
      )}
    </div>
  );
};

export default withDocumentTitle(ShortageReport, {
  pageTitle: "Discrepancy Reports",
});
